import { useState, useCallback } from "react";
import { apiGet, apiPost, apiPut, apiDelete } from "@/config";

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export function useArticles() {
  const [articles, setArticles] = useState([]);
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchArticles = useCallback(async (page = 1) => {
    try {
      const { data } = await apiGet(`/articles?page=${page}&limit=1`);
      setArticles(data.articles || []);
      setTotalPages(data.pages || 1);
    } catch {
      setArticles([]);
    }
  }, []);

  const fetchFeaturedArticles = useCallback(async () => {
    try {
      const { data } = await apiGet("/articles/featured/list");
      setFeaturedArticles(data);
    } catch {
      setFeaturedArticles([]);
    }
  }, []);

  const refreshAll = useCallback((page) => {
    fetchArticles(page);
    fetchFeaturedArticles();
  }, [fetchArticles, fetchFeaturedArticles]);

  const saveArticle = useCallback(async (articleData, editingId) => {
    if (editingId) {
      await apiPut(`/articles/${editingId}`, articleData);
    } else {
      await apiPost("/articles", articleData);
    }
  }, []);

  const deleteArticle = useCallback(async (articleId) => {
    await apiDelete(`/articles/${articleId}`);
  }, []);

  const fetchArchivedArticles = useCallback(async () => {
    const { data } = await apiGet("/articles/archived");
    return data;
  }, []);

  const unarchiveAll = useCallback(async () => {
    const { data } = await apiPost("/articles/unarchive-all");
    return data.message;
  }, []);

  const unarchiveArticle = useCallback(async (articleId) => {
    const { data } = await apiPost(`/articles/${articleId}/unarchive`);
    return data.message;
  }, []);

  const deleteAllArticles = useCallback(async () => {
    const { data } = await apiDelete("/articles/delete-all");
    return data.message;
  }, []);

  const importDocx = useCallback(async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await apiPost("/articles/import-docx", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  }, []);

  return {
    articles, featuredArticles, currentPage, setCurrentPage, totalPages,
    fetchArticles, fetchFeaturedArticles, refreshAll,
    saveArticle, deleteArticle, fetchArchivedArticles, unarchiveAll, unarchiveArticle, deleteAllArticles,
    importDocx, formatApiErrorDetail,
  };
}
